/** Webhook subscriptions + deliveries (Phase E1).
 *
 * 3 secции на странице:
 *  - Subscriptions: CRUD списка (url + event types + secret)
 *  - Deliveries: последние попытки доставки (status + retry button)
 *  - Events: append-only log domain events
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  CheckCircle2,
  Plus,
  RefreshCw,
  Send,
  Trash2,
  Webhook as WebhookIcon,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useDeleteConfirm } from "@/hooks/useDeleteConfirm";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

interface Subscription {
  id: number;
  name: string;
  target_url: string;
  event_types: string[];
  secret: string | null;
  is_active: boolean;
  extra_headers: Record<string, string>;
  created_at: string;
  updated_at: string;
}

interface Delivery {
  id: number;
  subscription_id: number;
  event_id: number;
  status: "pending" | "success" | "failed" | "retrying";
  response_code: number | null;
  response_body: string | null;
  attempt_count: number;
  next_retry_at: string | null;
  created_at: string;
  updated_at: string;
}

interface EventLogItem {
  id: number;
  event_type: string;
  subject_type: string;
  subject_id: number | null;
  payload: Record<string, unknown>;
  actor_type: string;
  actor_id: number | null;
  created_at: string;
}

const KNOWN_EVENT_TYPES = [
  "order.created",
  "order.status_changed",
  "order.cancelled",
  "payment.captured",
  "payment.refunded",
  "product.created",
  "product.updated",
  "product.deleted",
  "category.created",
  "client.registered",
  "review.published",
];

const STATUS_COLOR: Record<Delivery["status"], string> = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-300",
  retrying: "bg-yellow-100 text-yellow-800 border-yellow-300",
  success: "bg-green-100 text-green-800 border-green-300",
  failed: "bg-red-100 text-red-800 border-red-300",
};

export function WebhooksPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<"subs" | "deliveries" | "events">("subs");

  const processNow = useMutation({
    mutationFn: async () => {
      const { data } = await api.post("/webhooks/process-pending");
      return data as { processed: number };
    },
    onSuccess: (data) => {
      toast.success(`Обработано: ${data.processed}`);
      queryClient.invalidateQueries({ queryKey: ["webhook-deliveries"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Не удалось"),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/configuration">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Конфигурация
          </Link>
        </Button>
      </div>

      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-heading flex items-center gap-2">
            <WebhookIcon className="w-6 h-6" />
            Webhooks
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Outbound подписки + лог событий + попытки доставки (HMAC-SHA256,
            exp backoff, max 5 attempts).
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => processNow.mutate()}
          disabled={processNow.isPending}
        >
          <RefreshCw className="w-4 h-4 mr-1" />
          Запустить worker
        </Button>
      </div>

      <div className="flex gap-2 border-b">
        {(["subs", "deliveries", "events"] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
              tab === t
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t === "subs" ? "Подписки" : t === "deliveries" ? "Доставки" : "События"}
          </button>
        ))}
      </div>

      {tab === "subs" && <SubscriptionsTab />}
      {tab === "deliveries" && <DeliveriesTab />}
      {tab === "events" && <EventsTab />}
    </div>
  );
}

function SubscriptionsTab(): JSX.Element {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<number | "new" | null>(null);

  const { data: subs, isLoading } = useQuery({
    queryKey: ["webhook-subs"],
    queryFn: async () => {
      const { data } = await api.get("/webhooks/subscriptions");
      return data as Subscription[];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/webhooks/subscriptions/${id}`);
    },
    onSuccess: () => {
      toast.success("Удалено");
      queryClient.invalidateQueries({ queryKey: ["webhook-subs"] });
    },
  });

  const { askDelete, dialog: deleteDialog } = useDeleteConfirm<number>({
    onConfirm: (id) => deleteMutation.mutateAsync(id),
    title: "Удалить подписку?",
  });

  if (isLoading) return <Skeleton className="h-64" />;

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button onClick={() => setEditingId("new")}>
          <Plus className="w-4 h-4 mr-1" />
          Создать подписку
        </Button>
      </div>

      {editingId === "new" && (
        <SubscriptionForm mode="create" onClose={() => setEditingId(null)} />
      )}

      {subs?.length === 0 && editingId !== "new" && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Подписок нет. Создайте первую, чтобы получать events.
          </CardContent>
        </Card>
      )}

      {deleteDialog}

      {subs?.map((s) => (
        <Card key={s.id}>
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <CardTitle className="text-base flex items-center gap-2">
                  {s.name}
                  {s.is_active ? (
                    <Badge variant="secondary" className="text-[10px] gap-1">
                      <CheckCircle2 className="w-3 h-3" /> active
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] gap-1">
                      <XCircle className="w-3 h-3" /> disabled
                    </Badge>
                  )}
                </CardTitle>
                <div className="text-xs text-muted-foreground font-mono mt-0.5 truncate">
                  {s.target_url}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {editingId === s.id ? (
              <SubscriptionForm
                mode="edit"
                subscription={s}
                onClose={() => setEditingId(null)}
              />
            ) : (
              <>
                <div className="flex flex-wrap gap-1">
                  {s.event_types.length === 0 ? (
                    <span className="text-xs text-muted-foreground">
                      все события
                    </span>
                  ) : (
                    s.event_types.map((et) => (
                      <Badge
                        key={et}
                        variant="outline"
                        className="text-[10px] font-mono"
                      >
                        {et}
                      </Badge>
                    ))
                  )}
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>
                    {s.secret ? "🔐 HMAC enabled" : "⚠ no secret"} ·{" "}
                    {Object.keys(s.extra_headers).length} custom headers
                  </span>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditingId(s.id)}
                    >
                      Редактировать
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => askDelete(s.id, s.name)}
                    >
                      <Trash2 className="w-3.5 h-3.5 text-destructive" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function SubscriptionForm({
  mode,
  subscription,
  onClose,
}: {
  mode: "create" | "edit";
  subscription?: Subscription;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [name, setName] = useState(subscription?.name ?? "");
  const [targetUrl, setTargetUrl] = useState(subscription?.target_url ?? "");
  const [secret, setSecret] = useState(subscription?.secret ?? "");
  const [isActive, setIsActive] = useState(subscription?.is_active ?? true);
  const [eventTypes, setEventTypes] = useState<string[]>(
    subscription?.event_types ?? [],
  );
  const [headersJson, setHeadersJson] = useState(
    JSON.stringify(subscription?.extra_headers ?? {}, null, 2),
  );

  const toggleEvent = (et: string) => {
    setEventTypes((prev) =>
      prev.includes(et) ? prev.filter((x) => x !== et) : [...prev, et],
    );
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      let headers: Record<string, string>;
      try {
        headers = JSON.parse(headersJson || "{}");
      } catch {
        throw new Error("Невалидный JSON в extra_headers");
      }
      const payload = {
        name: name.trim(),
        target_url: targetUrl.trim(),
        secret: secret.trim() || null,
        is_active: isActive,
        event_types: eventTypes,
        extra_headers: headers,
      };
      if (mode === "create") {
        await api.post("/webhooks/subscriptions", payload);
      } else if (subscription) {
        await api.patch(
          `/webhooks/subscriptions/${subscription.id}`,
          payload,
        );
      }
    },
    onSuccess: () => {
      toast.success("Сохранено");
      queryClient.invalidateQueries({ queryKey: ["webhook-subs"] });
      onClose();
    },
    onError: (e: any) =>
      toast.error(e?.message || e?.response?.data?.detail || "Ошибка"),
  });

  return (
    <Card className={mode === "edit" ? "border-primary/30" : undefined}>
      {mode === "create" && (
        <CardHeader>
          <CardTitle className="text-base">Новая подписка</CardTitle>
        </CardHeader>
      )}
      <CardContent className="space-y-3 pt-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Название *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Bitrix24 CRM sync"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Target URL *</Label>
            <Input
              value={targetUrl}
              onChange={(e) => setTargetUrl(e.target.value)}
              placeholder="https://example.com/webhook"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Secret (HMAC-SHA256 X-Signature)</Label>
          <Input
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            placeholder="пусто = без подписи"
          />
        </div>
        <div className="space-y-1.5">
          <Label>
            События ({eventTypes.length === 0 ? "все" : `${eventTypes.length} выбрано`})
          </Label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
            {KNOWN_EVENT_TYPES.map((et) => {
              const checked = eventTypes.includes(et);
              return (
                <label
                  key={et}
                  className="flex items-center gap-1.5 text-xs cursor-pointer p-1 rounded hover:bg-muted/30"
                >
                  <Checkbox
                    checked={checked}
                    onChange={() => toggleEvent(et)}
                  />
                  <span className="font-mono">{et}</span>
                </label>
              );
            })}
          </div>
          <p className="text-[10px] text-muted-foreground">
            Пусто = подписан на все события.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label>Custom headers (JSON)</Label>
          <Textarea
            rows={3}
            className="font-mono text-xs"
            value={headersJson}
            onChange={(e) => setHeadersJson(e.target.value)}
          />
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox
            checked={isActive}
            onChange={(e) =>
              setIsActive((e.target as HTMLInputElement).checked)
            }
          />
          Активна
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={!name.trim() || !targetUrl.trim()}
          >
            {mode === "create" ? "Создать" : "Сохранить"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function DeliveriesTab(): JSX.Element {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const { data: deliveries, isLoading } = useQuery({
    queryKey: ["webhook-deliveries", statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status_filter", statusFilter);
      const { data } = await api.get(
        `/webhooks/deliveries${params.toString() ? `?${params}` : ""}`,
      );
      return data as Delivery[];
    },
  });

  const retryMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.post(`/webhooks/deliveries/${id}/retry`);
    },
    onSuccess: () => {
      toast.success("Поставлено в очередь");
      queryClient.invalidateQueries({ queryKey: ["webhook-deliveries"] });
    },
  });

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <div className="w-48 space-y-1.5">
          <Label className="text-xs">Статус</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все</SelectItem>
              <SelectItem value="pending">pending</SelectItem>
              <SelectItem value="retrying">retrying</SelectItem>
              <SelectItem value="success">success</SelectItem>
              <SelectItem value="failed">failed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-48" />
      ) : deliveries?.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Доставок нет.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {deliveries?.map((d) => (
            <Card key={d.id}>
              <CardContent className="p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="font-mono text-xs">
                    #{d.id}
                  </Badge>
                  <Badge className={STATUS_COLOR[d.status]}>{d.status}</Badge>
                  <span className="text-xs text-muted-foreground">
                    sub #{d.subscription_id} · event #{d.event_id} · attempt {d.attempt_count}
                  </span>
                  {d.response_code != null && (
                    <Badge variant="outline" className="text-[10px]">
                      HTTP {d.response_code}
                    </Badge>
                  )}
                  <div className="flex-1" />
                  {(d.status === "failed" || d.status === "retrying") && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => retryMutation.mutate(d.id)}
                    >
                      <Send className="w-3 h-3 mr-1" />
                      Retry
                    </Button>
                  )}
                </div>
                {d.response_body && (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-muted-foreground">
                      Response body
                    </summary>
                    <pre className="mt-1 p-2 bg-muted/30 rounded text-[10px] overflow-auto max-h-40">
                      {d.response_body}
                    </pre>
                  </details>
                )}
                <div className="text-[10px] text-muted-foreground">
                  Создан: {new Date(d.created_at).toLocaleString("ru-RU")}
                  {d.next_retry_at && (
                    <> · Next retry: {new Date(d.next_retry_at).toLocaleString("ru-RU")}</>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function EventsTab(): JSX.Element {
  const [eventTypeFilter, setEventTypeFilter] = useState<string>("");

  const { data: events, isLoading } = useQuery({
    queryKey: ["webhook-events", eventTypeFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (eventTypeFilter) params.set("event_type", eventTypeFilter);
      params.set("limit", "100");
      const { data } = await api.get(`/webhooks/events?${params}`);
      return data as EventLogItem[];
    },
  });

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <div className="w-64 space-y-1.5">
          <Label className="text-xs">Тип события</Label>
          <Select value={eventTypeFilter || "all"} onValueChange={(v) => setEventTypeFilter(v === "all" ? "" : v)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все</SelectItem>
              {KNOWN_EVENT_TYPES.map((et) => (
                <SelectItem key={et} value={et}>
                  {et}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-48" />
      ) : events?.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Событий нет.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {events?.map((e) => (
            <Card key={e.id}>
              <CardContent className="p-3 space-y-1.5">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="font-mono text-xs">
                    #{e.id}
                  </Badge>
                  <Badge variant="secondary" className="font-mono text-xs">
                    {e.event_type}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {e.subject_type} #{e.subject_id ?? "—"} · by {e.actor_type}
                    {e.actor_id ? ` #${e.actor_id}` : ""}
                  </span>
                  <div className="flex-1" />
                  <span className="text-[10px] text-muted-foreground">
                    {new Date(e.created_at).toLocaleString("ru-RU")}
                  </span>
                </div>
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground">
                    Payload
                  </summary>
                  <pre className="mt-1 p-2 bg-muted/30 rounded text-[10px] overflow-auto max-h-40 font-mono">
                    {JSON.stringify(e.payload, null, 2)}
                  </pre>
                </details>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
